// eslint-disable-next-line max-classes-per-file
import './citation-plugin.css';

import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';

import { useAgentChat } from '@/components/agent-chat/context/AgentChatContext';
import { CitationComponent } from '@/components/agent-chat/messages/tiptap-extensions/Citations';
import { agentChatApiClient } from '@/services/api/agentChatService';
import { store } from '@/state/state';

import { EditorFinchContext } from '../common';

const {
  CKEditor: { Plugin },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

const EditorCitationComponent = ({
  citationId,
  matterId,
  workspaceId,
}: {
  citationId: string;
  matterId: string;
  workspaceId?: string;
}) => {
  const { setSelectedCitation } = useAgentChat();

  return (
    <CitationComponent
      citationId={citationId}
      matterId={matterId}
      workspaceId={workspaceId}
      setSelectedCitation={setSelectedCitation}
    />
  );
};

export class CitationPlugin extends Plugin {
  init() {
    const { matterId, workspaceId, registerPortalTarget } =
      this.editor.config.get('finch_context') as EditorFinchContext;
    const { schema } = this.editor.model;

    // Register a custom inline object for CK Editor
    schema.register('citation', {
      isInline: true,
      isObject: true,
      allowWhere: '$text',
      allowAttributes: ['citation-id'],
    });

    const { conversion } = this.editor;

    // Conversion from HTML -> Model
    conversion.for('upcast').elementToElement({
      // Define what the HTML element looks like:
      // name: html tag type
      // attributes: any attributes on the tag that we want to parse
      view: { name: 'cite', attributes: { 'citation-id': true } },
      model: (viewElement, { writer }) => {
        return writer.createElement('citation', {
          'citation-id': viewElement.getAttribute('citation-id'),
        });
      },
    });

    // Conversion from Model -> HTML (for data/export)
    conversion.for('dataDowncast').elementToElement({
      model: 'citation',
      view: (modelElement, { writer }) => {
        const citationId = modelElement.getAttribute('citation-id') as string;
        const attrs: Record<string, string | null> = {
          'citation-id': citationId,
        };
        // Read cached citation data (including bates) from RTK Query store if available
        console.log('[BATES DEBUG] dataDowncast called for citation:', {
          citationId,
          matterId,
          workspaceId,
        });
        if (citationId && matterId) {
          try {
            const selector =
              agentChatApiClient.endpoints.getAgentChatCitation.select({
                matterId,
                citationId,
                workspaceId,
              });
            const result = selector(store.getState());
            const bates = result.data?.bates_number;
            console.log('[BATES DEBUG] RTK Query cache result:', {
              citationId,
              workspaceId,
              hasCachedData: !!result.data,
              bates_number: bates,
              citationName: result.data?.name,
              status: result.status,
              fullResult: result,
            });
            attrs['bates-number'] =
              bates || `MISSING EXHIBIT: ${result.data?.name}`;
          } catch (err) {
            console.error('[BATES DEBUG] Error reading cache:', err);
            attrs['bates-number'] = `Error getting bates number`;
          }
        } else {
          console.warn('[BATES DEBUG] Missing citationId or matterId:', {
            citationId,
            matterId,
          });
        }
        console.log('[BATES DEBUG] Final attrs for cite element:', attrs);
        return writer.createEmptyElement('cite', attrs);
      },
    });

    // Conversion for editing (renders React view)
    conversion.for('editingDowncast').elementToElement({
      model: 'citation',

      view: (modelEl, { writer }) => {
        const citationElement = writer.createRawElement(
          'span',
          {
            class: 'agent-chat-citation',
          },
          (domElement) => {
            const unregisterPortalTarget = registerPortalTarget({
              node: (
                <EditorCitationComponent
                  citationId={modelEl.getAttribute('citation-id') as string}
                  matterId={matterId}
                  workspaceId={workspaceId}
                />
              ),
              target: domElement,
            });

            domElement.addEventListener('destroy', unregisterPortalTarget);

            return domElement;
          },
        );

        return citationElement;
      },
    });
  }
}
